import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListUnitsResult } from '@whinerp/client';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * The unit of measure a product is counted in (F-161).
 *
 * Only base units are offered: a box of twelve is a way of selling a product
 * counted in each, not what it is counted in. With no base units defined yet
 * the panel says where they are made instead of drawing an empty picker.
 */
export function ProductUnitPanel({ productId, baseUnitId }: { productId: string; baseUnitId: string | null }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const measuring = useEntitlement('F-161');
  const units = useQuery<ListUnitsResult, ApiError>({
    queryKey: ['units'],
    queryFn: () => api.listUnits({}),
    enabled: measuring.enabled,
  });
  const save = useMutation<unknown, ApiError, string | null>({
    mutationFn: (unitId) => api.setProductBaseUnit({ path: { productId }, body: { unitId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['product', productId] }),
  });

  if (!measuring.enabled || !units.data) return null;
  const bases = units.data.units.filter((unit) => unit.baseUnitId === null);

  return (
    <Panel data-region="product-unit">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('unit.countedIn')}</SectionLabel>
        {save.error ? <p data-product-unit-refusal className="text-dense text-danger-text">{errorText(save.error)}</p> : null}
        {bases.length === 0 ? (
          <p data-product-unit-none className="text-meta text-ink-meta">{t('unit.noneYet')}</p>
        ) : (
          <select
            data-product-base-unit
            aria-label={t('unit.countedIn')}
            className="h-input w-panel-narrow rounded-control border border-control bg-surface px-2 text-body text-ink"
            value={baseUnitId ?? ''}
            disabled={save.isPending}
            onChange={(event) => save.mutate(event.target.value || null)}
          >
            <option value="">{t('unit.notCounted')}</option>
            {bases.map((unit) => (
              <option key={unit.unitId} value={unit.unitId}>
                {unit.label}
              </option>
            ))}
          </select>
        )}
        <p className="text-meta text-ink-meta">{t('unit.countedInHelp')}</p>
      </div>
    </Panel>
  );
}
